import { bearerAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import logo from "./logo.svg";

const sysdigDomains: Record<string, { current?: string; nextGen?: string }> = {
  us1: { current: "secure.sysdig.com", nextGen: "api.us1.sysdig.com" },
  us2: { current: "us2.app.sysdig.com", nextGen: "api.us2.sysdig.com" },
  us3: { current: "app.us3.sysdig.com", nextGen: "api.us3.sysdig.com" },
  us4: { current: "app.us4.sysdig.com", nextGen: "api.us4.sysdig.com" },
  eu1: { current: "eu1.app.sysdig.com", nextGen: "api.eu1.sysdig.com" },
  au1: { current: "app.au1.sysdig.com", nextGen: "api.au1.sysdig.com" },
  me1: { nextGen: "api.me2.sysdig.com" },
  in1: { nextGen: "api.in1.sysdig.com" },
  me2: { current: "app.me2.sysdig.com" },
};

function getSysdigDomains(region = ""): { current?: string; nextGen?: string } {
  return (
    sysdigDomains[region] ||
    sysdigDomains.us1 || { current: "secure.sysdig.com" }
  );
}

function sysdigPattern(region = ""): string {
  const domains = Object.values(getSysdigDomains(region)).filter(
    Boolean
  ) as string[];
  if (domains.length === 0) return "https://.*\\.sysdig\\.com/.*";
  const escaped = domains.map((domain) => domain.replace(/\./g, "\\."));
  return `https://(${escaped.join("|")})/.*`;
}

function getSysdigTestRequest(
  region = ""
): string | { url: string; expectedStatus: number | number[] } {
  const { current, nextGen } = getSysdigDomains(region);
  if (current) return `https://${current}/api/v1/users/me`;
  return {
    url: `https://${nextGen}`,
    expectedStatus: [200, 401, 403, 404],
  };
}

export const sysdig = defineConnector({
  identifier: "sysdig",
  name: "Sysdig",
  tagLine: "Cloud and container security",
  logo,
  protocol: "http",
  tags: ["security"],
  defaultUrlPattern: ({ region }) => sysdigPattern(region),
  auth: (app) => bearerAuth(app.secret.apiToken),
  steps: [
    {
      title: "Get your API token",
      items: [
        {
          kind: "markdown",
          content:
            "In Sysdig, hover over your username on the left panel and select **Sysdig API Tokens**. Copy the **Sysdig Secure API Token**.",
        },
      ],
    },
    {
      title: "Enter your Sysdig details",
      items: [
        {
          kind: "input",
          label: "API token",
          required: true,
          target: { kind: "secrets", field: "apiToken" },
        },
        {
          kind: "select",
          label: "Region",
          required: true,
          defaultValue: "us1",
          target: { kind: "configuration", field: "region" },
          helperText:
            "Choose the SaaS region where your Sysdig tenant is deployed.",
          options: [
            { label: "US-1", value: "us1" },
            { label: "US-2", value: "us2" },
            { label: "US-3", value: "us3" },
            { label: "US-4", value: "us4" },
            { label: "EU-1", value: "eu1" },
            { label: "AU-1", value: "au1" },
            { label: "ME-1", value: "me1" },
            { label: "IN-1", value: "in1" },
            { label: "ME-2", value: "me2" },
          ],
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: ({ region }) => getSysdigTestRequest(region),
  },
});
